import {
  EmailTemplateDetail,
  EmailTemplateKey,
  EmailTemplateListItem,
  EmailTemplatePreviewResult,
  EmailTemplateUpdateDto,
  EMAIL_TEMPLATE_KEYS
} from '../../../services/email-templates/email-template.model';

const OVERRIDES_STORAGE_KEY = 'htx-mock-email-template-overrides';

interface TemplateOverride {
  subject?: string;
  bodyHtml?: string;
}

interface TemplateDefault {
  defaultSubject: string;
  defaultBodyHtml: string;
}

/**
 * Default subjects mirror the backend enum
 * `de.adesso.htx.shared.email.domain.EmailTemplateKey`. Bodies are small stand-ins
 * for the real FreeMarker (.ftl) templates and use the same `${...}` placeholders.
 */
function body(title: string, inner: string): string {
  return [
    '<!DOCTYPE html>',
    '<html lang="de">',
    '  <body style="font-family: Arial, sans-serif; color: #1f2933; margin: 0; padding: 24px;">',
    '    <div style="max-width: 600px; margin: 0 auto; border: 1px solid #e4e7eb; border-radius: 8px; padding: 32px;">',
    `      <h1 style="font-size: 20px; color: #0b5fff;">${title}</h1>`,
    `      ${inner}`,
    '      <p style="margin-top: 32px; font-size: 12px; color: #7b8794;">HealthTrack-X Dataspace</p>',
    '    </div>',
    '  </body>',
    '</html>'
  ].join('\n');
}

const DEFAULTS: Record<EmailTemplateKey, TemplateDefault> = {
  'registration-approved': {
    defaultSubject: 'Willkommen auf HealthTrack-X',
    defaultBodyHtml: body(
      'Willkommen, ${firstName} ${lastName}!',
      '<p>Ihre Registrierung für <strong>${legalName}</strong> wurde genehmigt. Sie können sich jetzt am Portal anmelden.</p>'
    )
  },
  'registration-rejected': {
    defaultSubject: 'Ihre Registrierungsanfrage für {legalName} bei HealthTrack-X',
    defaultBodyHtml: body(
      'Registrierung abgelehnt',
      '<p>Leider konnten wir die Registrierung für <strong>${legalName}</strong> nicht genehmigen.</p><p>Begründung: ${reason}</p>'
    )
  },
  'usecase-participation-approved': {
    defaultSubject: 'Use-Case-Teilnahme genehmigt: {useCaseName} – HealthTrack-X',
    defaultBodyHtml: body(
      'Teilnahme genehmigt',
      '<p>Ihre Organisation <strong>${legalName}</strong> nimmt nun am Use-Case <strong>${useCaseName}</strong> teil.</p>'
    )
  },
  'usecase-participation-rejected': {
    defaultSubject: 'Use-Case-Teilnahme abgelehnt: {useCaseName} – HealthTrack-X',
    defaultBodyHtml: body(
      'Teilnahme abgelehnt',
      '<p>Die Teilnahme von <strong>${legalName}</strong> am Use-Case <strong>${useCaseName}</strong> wurde abgelehnt.</p><p>Begründung: ${reason}</p>'
    )
  },
  'representative-change-submitted': {
    defaultSubject: 'Vertretungswechsel beantragt – {legalName} – HealthTrack-X',
    defaultBodyHtml: body(
      'Vertretungswechsel beantragt',
      '<p>Für <strong>${legalName}</strong> wurde ein Wechsel des Vertretungsberechtigten beantragt.</p>'
    )
  },
  'representative-change-approved-new': {
    defaultSubject: 'Sie sind neuer Vertretungsberechtigter – {legalName} – HealthTrack-X',
    defaultBodyHtml: body(
      'Sie sind neuer Vertretungsberechtigter',
      '<p>Hallo ${firstName} ${lastName},</p><p>Sie sind ab sofort vertretungsberechtigt für <strong>${legalName}</strong>.</p>'
    )
  },
  'representative-change-rejected': {
    defaultSubject: 'Vertretungswechsel abgelehnt – {legalName} – HealthTrack-X',
    defaultBodyHtml: body(
      'Vertretungswechsel abgelehnt',
      '<p>Der beantragte Vertretungswechsel für <strong>${legalName}</strong> wurde abgelehnt.</p>'
    )
  },
  'organization-deleted': {
    defaultSubject: 'Ihre Organisation {legalName} wurde aus HealthTrack-X entfernt',
    defaultBodyHtml: body(
      'Organisation entfernt',
      '<p>Ihre Organisation <strong>${legalName}</strong> wurde aus dem Dataspace entfernt.</p>'
    )
  },
  'organization-reactivated': {
    defaultSubject: 'Änderung des Mitgliedsstatus bei HealthTrack-X',
    defaultBodyHtml: body(
      'Mitgliedschaft reaktiviert',
      '<p>Die Mitgliedschaft von <strong>${legalName}</strong> wurde reaktiviert.</p>'
    )
  },
  'organization-suspended': {
    defaultSubject: 'Änderung des Mitgliedsstatus bei HealthTrack-X',
    defaultBodyHtml: body(
      'Mitgliedschaft ausgesetzt',
      '<p>Die Mitgliedschaft von <strong>${legalName}</strong> wurde vorübergehend ausgesetzt.</p>'
    )
  }
};

/** Sample data used to render placeholders in the live preview. */
const SAMPLE_DATA: Record<string, string> = {
  firstName: 'Erika',
  lastName: 'Mustermann',
  legalName: 'Muster GmbH',
  shortName: 'Muster',
  useCaseName: 'Diabetes-Forschung',
  reason: 'Unvollständige Angaben'
};

let overrides: Record<string, TemplateOverride> = {};

function loadOverrides(): void {
  const stored = localStorage.getItem(OVERRIDES_STORAGE_KEY);
  if (stored) {
    try {
      overrides = JSON.parse(stored);
    } catch (e) {
      console.error('Failed to load email template overrides:', e);
      overrides = {};
    }
  }
}

function saveOverrides(): void {
  localStorage.setItem(OVERRIDES_STORAGE_KEY, JSON.stringify(overrides));
}

loadOverrides();

function isOverridden(key: EmailTemplateKey): boolean {
  const o = overrides[key];
  return !!o && (o.subject !== undefined || o.bodyHtml !== undefined);
}

function effectiveSubject(key: EmailTemplateKey): string {
  return overrides[key]?.subject ?? DEFAULTS[key].defaultSubject;
}

function effectiveBody(key: EmailTemplateKey): string {
  return overrides[key]?.bodyHtml ?? DEFAULTS[key].defaultBodyHtml;
}

/** Replace `${name}` and `{name}` placeholders with sample values. */
function renderPlaceholders(input: string): string {
  return input.replace(/\$?\{([a-zA-Z0-9_]+)\}/g, (match, name: string) => {
    return Object.prototype.hasOwnProperty.call(SAMPLE_DATA, name) ? SAMPLE_DATA[name] : match;
  });
}

export function listEmailTemplates(): EmailTemplateListItem[] {
  loadOverrides();
  return EMAIL_TEMPLATE_KEYS.map((key) => ({
    key,
    subject: effectiveSubject(key),
    overridden: isOverridden(key)
  }));
}

export function getEmailTemplate(key: EmailTemplateKey): EmailTemplateDetail | undefined {
  if (!DEFAULTS[key]) {
    return undefined;
  }
  loadOverrides();
  return {
    key,
    subject: effectiveSubject(key),
    defaultSubject: DEFAULTS[key].defaultSubject,
    bodyHtml: effectiveBody(key),
    defaultBodyHtml: DEFAULTS[key].defaultBodyHtml,
    overridden: isOverridden(key)
  };
}

export function updateEmailTemplate(
  key: EmailTemplateKey,
  dto: EmailTemplateUpdateDto
): EmailTemplateDetail | undefined {
  if (!DEFAULTS[key]) {
    return undefined;
  }
  loadOverrides();
  const existing = overrides[key] ?? {};
  overrides[key] = {
    subject: dto.subject ?? existing.subject,
    bodyHtml: dto.bodyHtml ?? existing.bodyHtml
  };
  saveOverrides();
  return getEmailTemplate(key);
}

export function resetEmailTemplate(key: EmailTemplateKey): EmailTemplateDetail | undefined {
  if (!DEFAULTS[key]) {
    return undefined;
  }
  loadOverrides();
  delete overrides[key];
  saveOverrides();
  return getEmailTemplate(key);
}

export function previewEmailTemplate(
  key: EmailTemplateKey,
  dto: EmailTemplateUpdateDto
): EmailTemplatePreviewResult | undefined {
  if (!DEFAULTS[key]) {
    return undefined;
  }
  const subject = dto.subject ?? effectiveSubject(key);
  const bodyHtml = dto.bodyHtml ?? effectiveBody(key);
  return {
    subject: renderPlaceholders(subject),
    bodyHtml: renderPlaceholders(bodyHtml)
  };
}
